import React, { useState, useEffect } from "react";
import { ArrowLeft, Upload, Check, AlertCircle, Calendar, MapPin, Download } from "lucide-react";
import { Button } from "components/ui/button";
import {
  previewProjectCsv,
  bulkSaveProjects,
} from "services/projectService";
import { getCrewRoles, getSkills } from "services/crewService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import MultiSelect from "components/ui/multiSelect";
import { getEventTypes } from "lib/api/requirementsApi";

const BulkUploadProject = ({ onBack, onComplete }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Master Data States
  const [eventTypes, setEventTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [skillsMaster, setSkillsMaster] = useState([]);

  const Streaming_options = [
    { id: "1", name: "Twitch" },
    { id: "2", name: "YouTube" },
    { id: "3", name: "Facebook" },
    { id: "4", name: "Twitter" },
    { id: "5", name: "LinkedIn" },
    { id: "6", name: "TikTok" },
  ];

  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [etRes, rolesRes, skillsRes] = await Promise.all([
          getEventTypes(),
          getCrewRoles(),
          getSkills(),
        ]);

          const rawEvents = etRes?.data || [];
        const normalizedEvents = rawEvents.map(t => ({
          id: t.event_type_id,
          name: t.event_type_name
        }));
        setEventTypes(normalizedEvents);
        console.log("Normalized Event Types:", normalizedEvents);

        console.log("Raw events Response:", etRes);

        const rawRoles = rolesRes?.data?.data || rolesRes?.data || rolesRes || [];
        const normalizedRoles = rawRoles.map(r => ({
          id: r.role_id || r.id,
          name: r.role_name || r.name
        }));
        setRoles(normalizedRoles);
        console.log("Normalized Roles:", normalizedRoles);

        // Normalize Skills (Mapping skill_id -> id, skill_name -> name)
        const rawSkills = skillsRes?.data?.data || skillsRes?.data || skillsRes || [];
        const normalizedSkills = rawSkills.map(s => ({
          id: s.skill_id || s.id,
          name: s.skill_name || s.name
        }));
        setSkillsMaster(normalizedSkills);

      } catch (err) {
        console.error("Failed to fetch master data", err);
      }
    };
    fetchMasters();
  }, []);

   const handleDownloadTemplate = () => {
    const templatePath = "/templates/project_template.csv";
    const link = document.createElement("a");
    link.href = templatePath;
    link.setAttribute("download", "project_upload_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRowUpdate = (index, field, value) => {
    const updatedData = [...previewData];
    updatedData[index][field] = value;
    setPreviewData(updatedData);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError(null);
  };

  const handleUploadPreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await previewProjectCsv(formData);
      const responseBody = res.data || res;

      let dataToMap = null;
      if (Array.isArray(responseBody)) {
        dataToMap = responseBody;
      } else if (responseBody && Array.isArray(responseBody.data)) {
        dataToMap = responseBody.data;
      }

      if (dataToMap) {
        const initialData = dataToMap.map((item) => ({
          ...item,
          event_type: item.event_type ? String(item.event_type) : "",
          streaming_platforms: [],
          crew_roles: [],
          skills_needed: [],
        }));
        setPreviewData(initialData);
        setStep(2);
      } else {
        setError(responseBody?.message || "No data found in CSV response.");
      }
    } catch (err) {
      setError("Error uploading file.");
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSave = async () => {
    setLoading(true);
    try {
      const res = await bulkSaveProjects({ project_list: previewData });
      const resData = res.data || res;
      if (!resData.error) {
        onComplete();
      } else {
        setError(resData.message || "Failed to save projects.");
      }
    } catch (err) {
      setError("Server error during save.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 w-full max-w-screen-xl mx-auto overflow-hidden">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} size="icon">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Bulk Upload Projects</h1>
            <p className="text-muted-foreground text-sm">Import multiple shoot requirements via CSV</p>
          </div>
        </div>

        {/* Header Download Button */}
        <Button
          variant="outline"
          onClick={handleDownloadTemplate}
          className="hidden md:flex items-center gap-2 border-primary text-primary hover:bg-primary/5"
        >
          <Download size={16} />
          Download Template
        </Button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg flex items-center gap-2">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {step === 1 ? (
        <div className="bg-card border-2 border-dashed border-border rounded-xl p-12 text-center">
          <Upload className="mx-auto w-12 h-12 text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold mb-2">Select Project CSV</h2>

          {/* New informative text for the user */}
          <p className="text-sm text-muted-foreground mb-6">
            Please use our official template to ensure data compatibility.<br/>
            <button
              onClick={handleDownloadTemplate}
              className="text-primary font-medium hover:underline inline-flex items-center gap-1 mt-1"
            >
              Download project_template.csv
            </button>
          </p>

          <input type="file" accept=".csv" onChange={handleFileChange} className="hidden" id="project-csv" />
          <label htmlFor="project-csv">
            <Button asChild variant="outline" className="cursor-pointer">
              <span>{file ? file.name : "Choose File"}</span>
            </Button>
          </label>
          <div className="mt-8">
            <Button className="bg-button-coloured px-8" disabled={!file || loading} onClick={handleUploadPreview}>
              {loading ? "Parsing..." : "Next: Map Requirements"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl shadow-sm flex flex-col overflow-hidden w-full">
          <div className="w-full overflow-x-auto no-scrollbar scrollbar-thin scrollbar-thumb-gray-300">
            <table className="min-w-full text-sm text-left border-collapse table-auto">
              <thead className="bg-muted/50 text-muted-foreground font-medium border-b">
                <tr>
                  <th className="px-6 py-4 whitespace-nowrap">Project Info</th>
                  <th className="px-6 py-4 whitespace-nowrap">Event Type (Single)</th>
                  <th className="px-6 py-4 whitespace-nowrap">Platforms</th>
                  <th className="px-6 py-4 whitespace-nowrap">Crew Roles</th>
                  <th className="px-6 py-4 whitespace-nowrap">Skills Needed</th>
                  <th className="px-6 py-4 text-center whitespace-nowrap">Budget</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-border">
                {previewData.map((item, idx) => (
                  <tr key={idx} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="font-semibold text-sm">{item.project_name}</div>
                      <div className="text-[10px] flex flex-col text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><Calendar size={10} /> {item.event_date}</span>
                        <span className="flex items-center gap-1 truncate max-w-[200px]" title={typeof item.event_location === 'object' ? item.event_location.address : item.event_location}>
                          <MapPin size={10} /> {typeof item.event_location === 'object' ? item.event_location.address : item.event_location}
                        </span>
                      </div>
                    </td>

                    {/* EVENT TYPE - SINGLE SELECT */}
                    <td className="px-6 py-3 min-w-[180px]">
                      <Select
                        value={String(item.event_type)}
                        onValueChange={(val) => handleRowUpdate(idx, "event_type", val)}
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {eventTypes.map((t) => (
                            <SelectItem key={t.id} value={String(t.id)}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>

                    {/* PLATFORMS - MULTI SELECT */}
                    <td className="px-6 py-3 min-w-[200px]">
                      <MultiSelect
                        placeholder="Platforms"
                        options={Streaming_options}
                        value={item.streaming_platforms.map(String)}
                        onChange={(vals) => handleRowUpdate(idx, "streaming_platforms", vals)}
                      />
                    </td>

                    {/* ROLES - MULTI SELECT */}
                    <td className="px-6 py-3 min-w-[200px]">
                      <MultiSelect
                        placeholder="Roles"
                        options={roles}
                        value={item.crew_roles.map(String)}
                        onChange={(vals) => handleRowUpdate(idx, "crew_roles", vals)}
                      />
                    </td>

                    {/* SKILLS - MULTI SELECT */}
                    <td className="px-6 py-3 min-w-[200px]">
                      <MultiSelect
                        placeholder="Skills"
                        options={skillsMaster}
                        value={item.skills_needed.map(String)}
                        onChange={(vals) => handleRowUpdate(idx, "skills_needed", vals)}
                      />
                    </td>

                    <td className="px-6 py-3 text-center font-medium whitespace-nowrap">
                      ${item.budget}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t flex justify-between items-center bg-muted/10">
            <Button variant="outline" onClick={() => setStep(1)}>Change File</Button>
            <div className="flex items-center gap-4 md:gap-6">
              <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                Total Projects: <span className="text-foreground">{previewData.length}</span>
              </span>
              <Button className="bg-black text-white hover:bg-black/90 px-6" onClick={handleFinalSave} disabled={loading}>
                {loading ? "Saving..." : <><Check className="mr-2 h-4 w-4" /> Import Projects</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUploadProject;
